import * as l from "@cerulea/lexicon";
import { Block, Channel, Follow } from "../structs.ts";
import type { ChannelView } from "../queries.ts";
import { RpcClient } from "./api.ts";
import { authState, pdsTransportFor } from "./auth.ts";
import { patchOwnProfile } from "./own-profile.ts";
import { listAll, recordRpcs } from "./pds.ts";
import { canWriteCollection } from "./permissions.ts";
import { createTid, rkeyOf } from "./repo.ts";

const record = l.union({
  [Channel.id]: Channel.record,
  [Follow.id]: Follow.record,
  [Block.id]: Block.record,
});
const graph = recordRpcs(l.enumValues(Channel.id, Follow.id, Block.id), record);

/** The schema only bounds length; render an accent only when it is a real hex color. */
export function accentColor(value: string | undefined): string | undefined {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : undefined;
}

export function ownRecords(
  viewer: `did:${string}`,
  collection: typeof Channel.id | typeof Follow.id | typeof Block.id,
  signal?: AbortSignal,
) {
  return listAll(new RpcClient(pdsTransportFor(viewer)), graph, viewer, collection, signal);
}

export async function ownChannels(
  viewer: `did:${string}`,
  signal?: AbortSignal,
): Promise<ChannelView[]> {
  const records = await ownRecords(viewer, Channel.id, signal);
  return records.flatMap((entry) =>
    entry.value.$type === Channel.id
      ? [{ uri: entry.uri, cid: entry.cid, record: entry.value }]
      : []
  );
}

export async function saveChannel(
  viewer: `did:${string}`,
  rkey: string,
  record: Channel,
  cid?: string,
  signal?: AbortSignal,
): Promise<void> {
  const state = authState.get();
  if (
    state.status !== "authenticated" ||
    !canWriteCollection(state.scopes, Channel.id, cid ? "update" : "create")
  ) throw new Error("Authorize channel management first");
  const written = await new RpcClient(pdsTransportFor(viewer)).call(graph.put, {
    parameters: {},
    input: {
      repo: viewer,
      collection: Channel.id,
      rkey,
      record,
      ...(cid && { swapRecord: cid }),
    },
    ...(signal && { signal }),
  });
  const view = { uri: written.uri, cid: written.cid, record };
  patchOwnProfile(viewer, (profile) => ({
    ...profile,
    channels: profile.channels.some((entry) => entry.uri === view.uri)
      ? profile.channels.map((entry) => entry.uri === view.uri ? view : entry)
      : [view, ...profile.channels],
  }));
}

export async function removeChannel(
  viewer: `did:${string}`,
  channel: ChannelView,
  signal?: AbortSignal,
): Promise<void> {
  const state = authState.get();
  if (state.status !== "authenticated" || !canWriteCollection(state.scopes, Channel.id, "delete")) {
    throw new Error("Authorize channel management first");
  }
  await new RpcClient(pdsTransportFor(viewer)).call(graph.delete, {
    parameters: {},
    input: {
      repo: viewer,
      collection: Channel.id,
      rkey: rkeyOf(channel.uri),
      swapRecord: channel.cid,
    },
    ...(signal && { signal }),
  });
  patchOwnProfile(viewer, (profile) => ({
    ...profile,
    channels: profile.channels.filter((entry) => entry.uri !== channel.uri),
  }));
}

/** Idempotently create or remove the viewer's follow/block records pointing at `actor`. */
export async function setRelation(
  viewer: `did:${string}`,
  collection: typeof Follow.id | typeof Block.id,
  actor: `did:${string}`,
  on: boolean,
  channel?: string,
  signal?: AbortSignal,
): Promise<void> {
  const state = authState.get();
  if (
    state.status !== "authenticated" ||
    !canWriteCollection(state.scopes, collection, on ? "create" : "delete")
  ) throw new Error("Authorize this action first");
  const existing = (await ownRecords(viewer, collection, signal)).filter((entry) =>
    entry.value.$type === collection && entry.value.subject === actor &&
    ("channel" in entry.value ? entry.value.channel : undefined) === channel
  );
  const client = new RpcClient(pdsTransportFor(viewer));
  if (on && !existing.length) {
    await client.call(graph.create, {
      parameters: {},
      input: {
        repo: viewer,
        collection,
        rkey: createTid(),
        record: {
          $type: collection,
          subject: actor,
          ...(channel && { channel }),
          createdAt: new Date().toISOString(),
        },
      },
      ...(signal && { signal }),
    });
  } else if (!on) {
    for (const entry of existing) {
      await client.call(graph.delete, {
        parameters: {},
        input: {
          repo: viewer,
          collection,
          rkey: rkeyOf(entry.uri),
          swapRecord: entry.cid,
        },
        ...(signal && { signal }),
      });
    }
  }
}
